import { doc, setDoc, getDoc } from 'firebase/firestore';
// import { ref } from 'vue'; // removed, not used
import { db } from '@/firebaseConfig';
import { TOURNAMENT_PAYOUTS_DB } from '@/constants/constants';

// Dynamic rounding function based on rounding method
function roundAmount(amount, roundingMethod) {
  switch (roundingMethod) {
    case 'roundToNearestDollar':
      return Math.round(amount);
    case 'roundToNearestFiveDollars':
      return Math.round(amount / 5) * 5;
    case 'roundToNearestTenDollars':
      return Math.round(amount / 10) * 10;
    default:
      return Math.round(amount); // Default to nearest dollar
  }
}

// Helper to select the appropriate payout table based on total entries
function selectPayoutTable(payoutTables, totalEntries) {
  if (!payoutTables || !Array.isArray(payoutTables) || payoutTables.length === 0) {
    return null;
  }
  
  // Find the table that covers the current entry count
  for (const table of payoutTables) {
    if (totalEntries >= table.minEntries && totalEntries <= table.maxEntries) {
      return table;
    }
  }
  
  // If no table found, return the last table (usually has the highest maxEntries)
  return payoutTables[payoutTables.length - 1];
}

// Main composable function
export function usePayouts() {
  // Get payout percentages for a user
  async function getPayoutPercentages(userId) {
    if (!userId) return null;
    const payoutsDoc = doc(db, TOURNAMENT_PAYOUTS_DB, userId);
    const docSnap = await getDoc(payoutsDoc);
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (Array.isArray(data.payoutPercentages)) {
        return data.payoutPercentages;
      }
    }
    return null;
  }

  // Recalculate and save payouts to Firestore
  async function recalculateAndSavePayouts({
    userId,
    tournamentId,
    players,
    buyinAmount,
    rebuyAmount,
    addonAmount,
    rebuyAllowed,
    addonAllowed,
    payoutTables,
    buyinRake = 0,
    rebuyRake = 0,
    addonRake = 0,
    // Allow passing pre-calculated values for manual mode
    numPlayers,
    numRebuys,
    totalPot
  }) {
    if (!tournamentId) return; // Change validation
    // Calculate totals
    let totalPrizePool;
    let totalEntries;

    if (players) {
      const totalPlayers = players.length;
      const totalRebuys = players.reduce((total, player) => total + (player.rebuys || 0), 0);
      const totalAddOns = players.reduce((total, player) => total + (player.addOn ? 1 : 0), 0);
      totalPrizePool = totalPlayers * (buyinAmount - buyinRake);
      if (rebuyAllowed) totalPrizePool += totalRebuys * (rebuyAmount - rebuyRake);
      if (addonAllowed) totalPrizePool += totalAddOns * (addonAmount - addonRake);
      totalEntries = totalPlayers + totalRebuys;
    } else {
      totalPrizePool = totalPot;
      totalEntries = numPlayers + numRebuys;
    }
    
    // Calculate total entries (players + rebuys + add-ons)
    
    // Select the appropriate payout table based on total entries
    const selectedTable = selectPayoutTable(payoutTables, totalEntries);
    
    if (!selectedTable) {
      console.warn('No payout table found for', totalEntries, 'entries');
      return;
    }
    
    // Check if using percent or dollar amounts
    const usePercent = selectedTable.usePercent !== undefined ? selectedTable.usePercent : true; // Default to true for backward compatibility
    
    const n = selectedTable.numberOfPayouts || 3;
    let percentages;
    let roundedPayouts = [];
    const roundingMethod = selectedTable.rounding || 'roundToNearestDollar';
    
    if (usePercent) {
      // Use percentage-based payouts
      if (selectedTable.prizesTemplate && selectedTable.prizesTemplate.length >= n) {
        // Use prizes template from selected table (convert from percentages to decimals)
        percentages = selectedTable.prizesTemplate.slice(0, n).map(p => p / 100);
      } else {
        // Use default percentages if no template
        percentages = (n === 2 ? [0.65, 0.35] : 
                      n === 3 ? [0.5, 0.3, 0.2] : 
                      Array(n).fill(0).map((_, i) => i < 3 ? [0.5, 0.3, 0.2][i] : 0.1 / (n - 3)));
      }
      
      // Use rounding method from selected table (roundingMethod already set above)
      
      // Calculate payouts from percentages
      let rawPayouts = [];
      for (let i = 0; i < n; i++) {
        const raw = totalPrizePool * percentages[i];
        const rounded = roundAmount(raw, roundingMethod);
        rawPayouts.push(raw);
        roundedPayouts.push(rounded);
      }
      
      // Adjust for rounding differences
      let sum = roundedPayouts.reduce((a, b) => a + b, 0);
      let diff = totalPrizePool - sum;
      
      if (diff !== 0) {
        // Determine the rounding step based on the rounding method
        let step;
        switch (roundingMethod) {
          case 'roundToNearestDollar':
            step = diff > 0 ? 1 : -1;
            break;
          case 'roundToNearestFiveDollars':
            step = diff > 0 ? 5 : -5;
            break;
          case 'roundToNearestTenDollars':
            step = diff > 0 ? 10 : -10;
            break;
          default:
            step = diff > 0 ? 1 : -1;
        }
        
        let indices = Array.from({ length: n }, (_, i) => i);
        indices.sort((a, b) => roundedPayouts[b] - roundedPayouts[a]);
        let absDiff = Math.abs(diff);
        let idx = 0;
        
        // Distribute the difference using the appropriate step size
        while (absDiff >= Math.abs(step)) {
          let i = indices[idx % indices.length];
          roundedPayouts[i] += step;
          absDiff -= Math.abs(step);
          idx++;
        }
        
        // If there's still a small difference, add it to the largest payout
        if (absDiff > 0) {
          roundedPayouts[indices[0]] += (diff > 0 ? absDiff : -absDiff);
        }
      }
    } else {
      // Use dollar amount-based payouts
      if (selectedTable.prizesTemplate && selectedTable.prizesTemplate.length >= n) {
        // Use prizes template directly as dollar amounts
        roundedPayouts = selectedTable.prizesTemplate.slice(0, n).map(amount => 
          roundAmount(amount, roundingMethod)
        );
        // For dollar amounts, we don't need percentages, but set it for backward compatibility
        percentages = roundedPayouts.map(amount => amount / totalPrizePool);
      } else {
        // Fallback: use default percentages if no template
        percentages = (n === 2 ? [0.65, 0.35] : 
                      n === 3 ? [0.5, 0.3, 0.2] : 
                      Array(n).fill(0).map((_, i) => i < 3 ? [0.5, 0.3, 0.2][i] : 0.1 / (n - 3)));
        for (let i = 0; i < n; i++) {
          const raw = totalPrizePool * percentages[i];
          roundedPayouts.push(roundAmount(raw, roundingMethod));
        }
      }
    }
    
    // Build payouts array
    const payouts = Array.from({ length: n }, (_, i) => ({ amount: roundedPayouts[i] }));
    // Save to Firestore, including payoutPercentages and selected table info
    const payoutsDoc = doc(db, TOURNAMENT_PAYOUTS_DB, tournamentId);
    await setDoc(payoutsDoc, {
      numberOfPayouts: n,
      payouts,
      payoutPercentages: percentages,
      totalPrizePool,
      totalEntries,
      selectedTableIndex: payoutTables ? payoutTables.indexOf(selectedTable) : -1,
      roundingMethod: roundingMethod,
      timestamp: Date.now(),
      userId // Store userId for reference
    });
    return payouts;
  }
  
  return { recalculateAndSavePayouts, getPayoutPercentages, selectPayoutTable };
}